import { useState } from 'react';
import { useT } from '../../i18n';
import { Dialog } from '../ui/Dialog';
import { cancelOwnBooking, type CancelSubject } from '../../lib/api/memberCancel';
import { relativeDayLabel } from './memberData';

// ═══════════════════════════════════════════════════════════════════
// «AVBESTILL» ON A RESERVATION (#1135 slice 4).
//
// Owner decision 2026-11-01: a member cancels their own booking before the
// club's deadline, paid included, with a full refund; after it, «For sent å
// avbestille — kontakt klubben». Everything this shows comes from the server:
// the bootstrap's `cancellable: { until, refundMinor, open }` decides whether
// the button is there and what the dialog promises, and the command's answer
// decides what happened. The app computes neither a deadline nor an amount.
//
// A DIALOG, NEVER `window.confirm()` (DOMAIN_RULES): the shared `Dialog`, so the
// confirmation is named, modal, keyboard-safe and gives focus back.
// ═══════════════════════════════════════════════════════════════════

export interface Cancellable {
  /** Venue-local `YYYY-MM-DDTHH:MM`. */
  readonly until: string;
  readonly refundMinor: number;
  readonly open: boolean;
}

/** Kroner for DISPLAY only — whole kroner without decimals, øre with two. */
function kroner(minor: number): string {
  const kr = minor / 100;
  return Number.isInteger(kr) ? String(kr) : kr.toFixed(2).replace('.', ',');
}

type Phase =
  { kind: 'closed' } | { kind: 'asking' } | { kind: 'working' } | { kind: 'message'; text: string };

export function CancelOwnBooking({
  id,
  subject = 'booking',
  cancellable,
  summary,
  onCancelled,
}: {
  id: string;
  subject?: CancelSubject;
  cancellable: Cancellable;
  /** What is being cancelled, in words — court and time. */
  summary: string;
  /** The server cancelled it; `refundMinor` is what goes back. */
  onCancelled: (refundMinor: number) => void;
}) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>({ kind: 'closed' });

  if (!cancellable.open) {
    return (
      <div className="mt-2 text-[12px] text-ink-2" data-testid="cancel-too-late">
        {t('mobile.reservations.cancel.tooLate')}
      </div>
    );
  }

  const [untilDate, untilTime] = cancellable.until.split('T');

  const confirm = async () => {
    setPhase({ kind: 'working' });
    const r = await cancelOwnBooking(id, subject);
    if (r.state === 'cancelled') {
      setPhase({ kind: 'closed' });
      onCancelled(r.refundMinor);
      return;
    }
    // ALREADY CANCELLED (round 9): another press, or another device, got there
    // first. That is the member's wish, done — the row says so.
    if (r.state === 'refused' && r.code === 'already_cancelled') {
      setPhase({ kind: 'closed' });
      onCancelled(0);
      return;
    }
    const code = r.state === 'refused' ? r.code : null;
    const text =
      r.state === 'unconfirmed' || code === 'refund_in_progress' || code === 'payment_unconfirmed'
        ? t('mobile.reservations.cancel.unconfirmed')
        : code === 'too_late'
          ? t('mobile.reservations.cancel.tooLate')
          : code === 'refund_failed'
            ? t('mobile.reservations.cancel.failed')
            : code === 'refunds_unavailable'
              ? t('mobile.reservations.cancel.unavailable')
              : t('mobile.reservations.cancel.error');
    setPhase({ kind: 'message', text });
  };

  return (
    <>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => setPhase({ kind: 'asking' })}
          className="text-[12px] font-semibold text-ink-2 underline underline-offset-2 focus-ring rounded"
        >
          {t('mobile.reservations.cancel.open')}
        </button>
      </div>
      {phase.kind !== 'closed' && (
        <Dialog
          heading={t('mobile.reservations.cancel.title')}
          onClose={() => {
            // Not while the request is out: closing then would hide the answer.
            if (phase.kind !== 'working') setPhase({ kind: 'closed' });
          }}
        >
          <p className="text-[14px] text-ink">{summary}</p>
          <p className="text-[13px] text-ink-2 mt-2" data-testid="cancel-refund">
            {cancellable.refundMinor > 0
              ? t('mobile.reservations.cancel.refund', { amount: kroner(cancellable.refundMinor) })
              : t('mobile.reservations.cancel.free')}
          </p>
          <p className="text-[12px] text-ink-3 mt-1">
            {t('mobile.reservations.cancel.until', {
              when: `${relativeDayLabel(untilDate ?? '')} ${untilTime ?? ''}`,
            })}
          </p>
          {phase.kind === 'message' && (
            <p role="alert" className="text-[13px] text-rose-700 mt-3">
              {phase.text}
            </p>
          )}
          <div className="flex gap-2 mt-4">
            {phase.kind !== 'message' && (
              <button
                type="button"
                onClick={() => void confirm()}
                disabled={phase.kind === 'working'}
                className="flex-1 rounded-xl px-3 py-2 text-[13px] font-semibold disabled:opacity-40"
                style={{ background: '#0f1611', color: '#ffffff' }}
              >
                {phase.kind === 'working'
                  ? t('mobile.reservations.cancel.working')
                  : t('mobile.reservations.cancel.confirm')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setPhase({ kind: 'closed' })}
              disabled={phase.kind === 'working'}
              className="rounded-xl px-3 py-2 text-[13px] border border-black/10 disabled:opacity-40"
            >
              {t('mobile.reservations.cancel.keep')}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
